import {
  ReactElement,
  useRef,
  useState,
  useEffect,
  useCallback,
  ChangeEvent,
  KeyboardEvent,
  forwardRef,
  useImperativeHandle,
} from "react";
import { getTitle } from "../../shared/utils";
import { PageNumberData } from "../../shared/types";
import { PageNumber } from "@bronte/common";
import {
  isLessonAudioFailed,
  localLessonJsonUrl,
  localLessonOpusUrl,
  NO_AUDIO_FOR_SCREEN_MESSAGE,
} from "../../shared/localCourseAudio";
import {
  findAudioStartMsForTextOffset,
  findLatestTtsWordAudioStartAtOrBefore,
  findTtsWordsStartedSince,
} from "../../shared/ttsSeekUtils";
import { getSpotlightApi } from "../../shared/spotlightApi";
import "./styles.scss";

const seekAndPlay = (el: HTMLVideoElement, seconds: number): void => {
  el.currentTime = seconds;
  void el.play().catch((err) => {
    console.warn(
      "Dev Shell spotlight audio: autoplay blocked — use the audio controls to press play",
      err,
    );
  });
};

// Seeking to (or past) the end fires "ended", which auto-advances to the next screen.
const canSeekTo = (el: HTMLMediaElement, seconds: number): boolean =>
  !Number.isFinite(el.duration) || seconds < el.duration;

export type NavigationBarHandle = {
  playTtsFromTextOffset: (dataElementId: string, textOffset: number) => boolean;
  startSpotlightAudioForCurrentScreen: () => boolean;
  /** Call synchronously from the spotlight-audio menu click (preserves user-gesture context). */
  armPlaybackFromUserGesture: () => void;
  /** Queue a seek target for the next time playback starts (e.g. while assets are still loading). */
  seekToTextOffsetOnceReady: (dataElementId: string, textOffset: number) => void;
};

export const NavigationBar = forwardRef<NavigationBarHandle, any>(function NavigationBar(
  {
    viewerApi,
    prevItem,
    nextItem,
    pageNumber,
    pageNumbersMap,
    renderedId,
    currentItem,
    routeCompleted,
    spotlightAudio,
    failedScreenIds,
    shouldNavigateByPage,
    isAudioPlayerVisible: _isAudioPlayerVisible = false,
    setAudioPlayerVisible = (): void => {},
  },
  ref,
): ReactElement {
  const audioRef = useRef<HTMLVideoElement | null>(null);
  const userPausedAudioRef = useRef(false); // user hit pause
  const navigatingRef = useRef(false); // we're changing route
  const audioEndedRef = useRef(false); // track finished
  const [ttsJSON, setTTSJSON] = useState<any>(null);
  const [audioURL, setAudioURL] = useState<string>("");
  const pendingSpotlightStartRef = useRef(false);
  const pendingSeekRef = useRef<{ dataElementId: string; textOffset: number } | null>(null);
  const userGestureArmedRef = useRef(false);
  const localAudioObjectUrlRef = useRef<string | null>(null);
  const failedIds = failedScreenIds ?? new Set<string>();
  const [targetPageNumber, setTargetPageNumber] = useState(pageNumber);
  const [pageNumberIndex, setPageNumberIndex] = useState(0);

  useEffect(() => {
    setTargetPageNumber(pageNumber);
  }, [pageNumber]);

  // When user navigates (e.g. via TOC or prev/next), stop audio and clear spotlight so we don't keep playing the old screen's audio
  const revokeLocalAudioObjectUrl = () => {
    if (localAudioObjectUrlRef.current) {
      URL.revokeObjectURL(localAudioObjectUrlRef.current);
      localAudioObjectUrlRef.current = null;
    }
  };

  useEffect(() => {
    navigatingRef.current = true;
    getSpotlightApi(viewerApi)?.clear();
    audioRef.current?.pause();
    revokeLocalAudioObjectUrl();
    setAudioURL("");
    setTTSJSON(null);
    pendingSeekRef.current = null;
  }, [renderedId, viewerApi]);

  const handleEnter = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      const { screenId, dataId } = getPageNumberMap(targetPageNumber) || {};

      if (screenId && dataId) {
        viewerApi.route(screenId, { dataId });

        if (shouldNavigateByPage) {
          const pageNumberList = getPageNumbersByScreen(screenId);
          const pageNumberIndex = getCurrentPageIndex(pageNumberList, targetPageNumber);
          setPageNumberIndex(Number(pageNumberIndex));
        }
      } else {
        setTargetPageNumber(pageNumber);
        alert(`Dev Shell: ${targetPageNumber} is not a valid page number.`);
        console.error(`Dev Shell: ${targetPageNumber} is not a valid page number.`);
      }
    }
  };

  const getPageNumberMap = (pageNumber: string): PageNumberData | undefined => {
    for (const screenId in pageNumbersMap) {
      for (const item of pageNumbersMap[screenId]) {
        if (item.dataId && item.pageNumber === pageNumber) {
          return {
            screenId,
            dataId: item.dataId,
          };
        }
      }
    }

    return;
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setTargetPageNumber(value);
  };

  const getCurrentPageIndex = (pageNumberList: PageNumber[], pageNumber: string): number => {
    const pageNumberIndex = pageNumberList.findIndex(
      (obj: PageNumber) => obj.pageNumber === pageNumber,
    );
    return pageNumberIndex;
  };

  const navigateToSlate = (
    currIndex: number,
    pageNumberArr: PageNumber[],
    elementId: string,
  ): void => {
    const currDataId = pageNumberArr[currIndex]?.dataId;
    if (currDataId) {
      viewerApi.route(currentItem, { dataId: currDataId });
      setPageNumberIndex(currIndex);
    } else {
      const currentPageNum = Number(targetPageNumber);
      const anchorPageNum = Number(pageNumberArr[currIndex]?.pageNumber);

      if (elementId === prevItem?.elementId && anchorPageNum < currentPageNum) {
        const prevPageArr = getPageNumbersByScreen(elementId);
        for (let i = prevPageArr.length - 1; i >= 0; i--) {
          const entry = prevPageArr[i];
          if (!entry?.dataId) continue;
          if (Number(entry.pageNumber) < currentPageNum) {
            viewerApi.route(elementId, { dataId: entry.dataId });
            setPageNumberIndex(i);
            return;
          }
          break;
        }
      }

      viewerApi.route(elementId);
      setPageNumberIndex(0);
    }
  };

  const getPageNumbersByScreen = (elementId: string): PageNumber[] => {
    const pageNumbers = pageNumbersMap && pageNumbersMap[elementId];
    return (shouldNavigateByPage && pageNumbers) || [];
  };

  const onNext = (): void => {
    const pageNumberArr = getPageNumbersByScreen(currentItem);
    let currIndex = pageNumberIndex || 0;
    const pageNumberArrLen = pageNumberArr.length;

    if (shouldNavigateByPage && pageNumberArrLen > 1 && currIndex !== pageNumberArrLen - 1) {
      currIndex = getCurrentPageIndex(pageNumberArr, targetPageNumber);
      navigateToSlate(currIndex + 1, pageNumberArr, nextItem.elementId);
      return;
    }

    if (nextItem?.elementId) {
      viewerApi.route(nextItem.elementId);
      setPageNumberIndex(0);
    }
  };

  const onPrev = (): void => {
    const pageNumberArr = getPageNumbersByScreen(currentItem);
    let currIndex = pageNumberIndex || 0;

    if (shouldNavigateByPage && pageNumberArr?.length > 1 && currIndex > 0) {
      currIndex = getCurrentPageIndex(pageNumberArr, targetPageNumber);
      navigateToSlate(currIndex - 1, pageNumberArr, prevItem.elementId);
      return;
    }

    if (prevItem?.elementId) {
      const pageNumberArr = getPageNumbersByScreen(prevItem.elementId);
      const pageNumberArrLen = pageNumberArr.length;

      if (shouldNavigateByPage && pageNumberArrLen > 1) {
        viewerApi.route(prevItem.elementId, {
          dataId: pageNumberArr[pageNumberArrLen - 1]?.dataId,
        });
        const currIndex = getCurrentPageIndex(pageNumberArr, targetPageNumber);
        setPageNumberIndex(currIndex - 1);
      } else {
        viewerApi.route(prevItem.elementId);
        setPageNumberIndex(0);
      }
    }
  };

  //=================================================================================
  // AUDIO
  const applyLoadedTts = (json: { selectorElements?: unknown[] }) => {
    setTTSJSON(json?.selectorElements ?? []);
  };

  const fetchTTSAssets = () => {
    if (!renderedId || !spotlightAudio) {
      console.log(
        "Dev Shell spotlight audio: skip fetch (missing renderedId or spotlight config)",
        {
          renderedId,
          hasSpotlightAudio: Boolean(spotlightAudio),
        },
      );
      return;
    }

    const localBase = spotlightAudio.localAudioBaseUrl;
    if (localBase) {
      if (isLessonAudioFailed(renderedId, failedIds)) {
        console.warn("Dev Shell spotlight audio: lesson in _failed.json, skipping", {
          renderedId,
        });
        return;
      }
      const jsonUrl = localLessonJsonUrl(localBase, renderedId);
      const opusUrl = localLessonOpusUrl(localBase, renderedId);
      console.log("Dev Shell spotlight audio: loading local TTS", {
        renderedId,
        metadataUrl: jsonUrl,
        audioUrl: opusUrl,
      });
      void Promise.all([
        fetch(jsonUrl).then((r) => {
          if (!r.ok) {
            throw new Error(`Local TTS JSON ${r.status}`);
          }
          return r.json();
        }),
        fetch(opusUrl).then((r) => {
          console.log("Dev Shell spotlight audio: opus fetch response", {
            audioUrl: opusUrl,
            status: r.status,
            ok: r.ok,
          });
          if (!r.ok) {
            throw new Error(`Local TTS opus ${r.status}`);
          }
          return r.blob();
        }),
      ])
        .then(([json, blob]) => {
          applyLoadedTts(json);
          revokeLocalAudioObjectUrl();
          const objectUrl = URL.createObjectURL(blob);
          localAudioObjectUrlRef.current = objectUrl;
          console.log("Dev Shell spotlight audio: assets ready", {
            audioUrl: opusUrl,
            blobUrl: objectUrl,
            blobBytes: blob.size,
          });
          setAudioURL(objectUrl);
        })
        .catch((e) => {
          console.error(
            "Dev Shell spotlight audio: fetch failed",
            { metadataUrl: jsonUrl, audioUrl: opusUrl },
            e,
          );
        });
      return;
    }

    if (spotlightAudio.ttsURL) {
      const mp3Url = `/proxy-tts?target=${spotlightAudio.ttsURL}/${renderedId}.mp3`;
      console.log("Dev Shell spotlight audio: loading remote TTS", {
        renderedId,
        audioUrl: mp3Url,
      });
      fetch(`/proxy-tts?target=${spotlightAudio.ttsURL}/${renderedId}.json`)
        .then((r) => r.json())
        .then((json) => {
          applyLoadedTts(json);
          console.log("Dev Shell spotlight audio: will play", { audioUrl: mp3Url });
          setAudioURL(mp3Url);
        })
        .catch((e) => {
          console.error("Fetch TTS assets error", e);
        });
    }
  };

  useEffect(() => {
    if (renderedId && spotlightAudio && routeCompleted) {
      fetchTTSAssets();
    } else if (spotlightAudio && !routeCompleted) {
      console.log("Dev Shell spotlight audio: waiting for route to complete before fetch", {
        renderedId,
      });
    }
  }, [spotlightAudio, routeCompleted, renderedId]);

  useEffect(() => {
    if (!spotlightAudio) {
      getSpotlightApi(viewerApi)?.clear();
      audioRef.current?.pause();
      revokeLocalAudioObjectUrl();
      setAudioURL("");
      setTTSJSON(null);
      userGestureArmedRef.current = false;
      pendingSpotlightStartRef.current = false;
      pendingSeekRef.current = null;
      userPausedAudioRef.current = false;
      setAudioPlayerVisible(false);
      return;
    }
    userPausedAudioRef.current = false;
    pendingSpotlightStartRef.current = true;
  }, [spotlightAudio, viewerApi, setAudioPlayerVisible]);

  useEffect(() => {
    const audioElement = audioRef.current;

    if (audioURL && ttsJSON) {
      navigatingRef.current = false;
    }

    let spotlightsCount = 0;
    let lastAppliedWordAudioStartMs = -1;
    let spotlightRafId: number | null = null;

    const applySpotlightMatch = (match: {
      dataId: string;
      sentence: { text?: string; textOffset: number; textLength: number };
      word: { text?: string; textOffset: number; textLength: number; startOffset: number };
    }): void => {
      const { dataId, sentence, word } = match;

      if (spotlightAudio?.maxSpotlights && spotlightsCount > spotlightAudio.maxSpotlights) {
        spotlightsCount = 0;
        handleAudioEnded();
        return;
      }
      spotlightsCount++;

      lastAppliedWordAudioStartMs = word.startOffset;

      getSpotlightApi(viewerApi)?.apply({
        selector: dataId,
        mark: [
          {
            startOffset: sentence.textOffset,
            length: sentence.textLength,
            text: sentence.text,
            backgroundColor: spotlightAudio?.sentenceHighlightColor ?? "beige",
          },
          {
            startOffset: word.textOffset,
            length: word.textLength,
            text: word.text,
            backgroundColor: spotlightAudio?.wordHighlightColor ?? "lightblue",
          },
        ],
      });
    };

    const syncSpotlightToAudioTime = (currentTimeMs: number): void => {
      if (!ttsJSON?.length) {
        return;
      }

      const matches = findTtsWordsStartedSince(ttsJSON, currentTimeMs, lastAppliedWordAudioStartMs);
      for (const match of matches) {
        applySpotlightMatch(match);
      }
    };

    const resetSpotlightSyncPosition = (currentTimeMs: number): void => {
      if (!ttsJSON?.length) {
        return;
      }
      lastAppliedWordAudioStartMs =
        findLatestTtsWordAudioStartAtOrBefore(ttsJSON, currentTimeMs) - 1;
      syncSpotlightToAudioTime(currentTimeMs);
    };

    const stopSpotlightRaf = (): void => {
      if (spotlightRafId != null) {
        cancelAnimationFrame(spotlightRafId);
        spotlightRafId = null;
      }
    };

    const tickSpotlight = (): void => {
      if (!audioElement || audioElement.paused || audioElement.ended) {
        spotlightRafId = null;
        return;
      }
      syncSpotlightToAudioTime(audioElement.currentTime * 1000);
      spotlightRafId = requestAnimationFrame(tickSpotlight);
    };

    const startSpotlightRaf = (): void => {
      if (spotlightRafId == null && audioElement && !audioElement.paused && !audioElement.ended) {
        spotlightRafId = requestAnimationFrame(tickSpotlight);
      }
    };

    const handleAudioPaused = () => {
      stopSpotlightRaf();
      getSpotlightApi(viewerApi)?.clear();
      const pausedBecauseEnded = audioEndedRef.current || audioElement?.ended === true;
      if (pausedBecauseEnded) audioEndedRef.current = false;
      if (!navigatingRef.current && !pausedBecauseEnded) {
        userPausedAudioRef.current = true;
      }
    };

    const handleAudioPlay = () => {
      userPausedAudioRef.current = false;
      if (audioElement && ttsJSON?.length) {
        resetSpotlightSyncPosition(audioElement.currentTime * 1000);
      }
      startSpotlightRaf();
    };

    const handleAudioSeeked = () => {
      if (audioElement && ttsJSON?.length && !audioElement.paused && !audioElement.ended) {
        resetSpotlightSyncPosition(audioElement.currentTime * 1000);
      }
    };

    const handleAudioEnded = () => {
      stopSpotlightRaf();
      lastAppliedWordAudioStartMs = -1;
      audioEndedRef.current = true;
      if (nextItem?.elementId && audioURL && !navigatingRef.current) {
        setAudioURL("");
        setTTSJSON(null);
        viewerApi.route(nextItem?.elementId);
      }
    };

    const handleAudioIsWaiting = () => {
      setAudioPlayerVisible(false);
    };

    if (audioElement) {
      audioElement.addEventListener("pause", handleAudioPaused);
      audioElement.addEventListener("play", handleAudioPlay);
      audioElement.addEventListener("seeked", handleAudioSeeked);
      audioElement.addEventListener("ended", handleAudioEnded);
      audioElement.addEventListener("waiting", handleAudioIsWaiting);
      if (!audioElement.paused && !audioElement.ended && ttsJSON?.length) {
        resetSpotlightSyncPosition(audioElement.currentTime * 1000);
        startSpotlightRaf();
      }
    }

    return () => {
      stopSpotlightRaf();
      if (audioElement) {
        audioElement.removeEventListener("pause", handleAudioPaused);
        audioElement.removeEventListener("play", handleAudioPlay);
        audioElement.removeEventListener("seeked", handleAudioSeeked);
        audioElement.removeEventListener("ended", handleAudioEnded);
        audioElement.removeEventListener("waiting", handleAudioIsWaiting);
      }
    };
  }, [audioURL, ttsJSON, viewerApi, nextItem, spotlightAudio]);

  const tryStartPlayback = useCallback((): void => {
    const el = audioRef.current;
    if (!el || !audioURL || !ttsJSON?.length) {
      return;
    }
    if (!pendingSpotlightStartRef.current && !userGestureArmedRef.current) {
      return;
    }

    const pendingSeek = pendingSeekRef.current;
    let startSeconds = 0;

    if (pendingSeek) {
      const ms = findAudioStartMsForTextOffset(
        ttsJSON,
        pendingSeek.dataElementId,
        pendingSeek.textOffset,
      );
      pendingSeekRef.current = null;
      if (ms == null || !canSeekTo(el, ms / 1000)) {
        console.warn("Dev Shell spotlight audio: could not resolve seek target", {
          ...pendingSeek,
          ms,
          durationMs: el.duration * 1000,
        });
        pendingSpotlightStartRef.current = false;
        userGestureArmedRef.current = false;
        return;
      }
      startSeconds = ms / 1000;
    }

    pendingSpotlightStartRef.current = false;
    userGestureArmedRef.current = false;
    userPausedAudioRef.current = false;
    navigatingRef.current = false;
    setAudioPlayerVisible(true);

    console.log("Dev Shell spotlight audio: calling play()", {
      src: el.src,
      startSeconds,
    });
    seekAndPlay(el, startSeconds);
  }, [audioURL, ttsJSON, setAudioPlayerVisible]);

  useEffect(() => {
    if (!audioURL || !ttsJSON?.length) {
      return;
    }
    const el = audioRef.current;
    if (!el) {
      return;
    }
    const onReady = (): void => {
      console.log("Dev Shell spotlight audio: media ready (canplaythrough)");
      tryStartPlayback();
    };
    el.addEventListener("canplaythrough", onReady, { once: true });
    el.load();
    return () => {
      el.removeEventListener("canplaythrough", onReady);
    };
  }, [audioURL, ttsJSON, tryStartPlayback]);

  useImperativeHandle(
    ref,
    () => ({
      playTtsFromTextOffset: (dataElementId: string, textOffset: number): boolean => {
        const audio = audioRef.current;
        if (!ttsJSON?.length || !audio || !audioURL) {
          return false;
        }
        const ms = findAudioStartMsForTextOffset(ttsJSON, dataElementId, textOffset);
        if (ms == null || !canSeekTo(audio, ms / 1000)) {
          console.warn("Dev Shell spotlight audio: no TTS match for selection", {
            dataElementId,
            textOffset,
          });
          return false;
        }
        userPausedAudioRef.current = false;
        navigatingRef.current = false;
        setAudioPlayerVisible(true);
        seekAndPlay(audio, ms / 1000);
        return true;
      },
      startSpotlightAudioForCurrentScreen: (): boolean => {
        if (!renderedId || !spotlightAudio) {
          return false;
        }
        if (isLessonAudioFailed(renderedId, failedIds)) {
          alert(NO_AUDIO_FOR_SCREEN_MESSAGE);
          return false;
        }
        userPausedAudioRef.current = false;
        navigatingRef.current = false;
        pendingSpotlightStartRef.current = true;
        setAudioPlayerVisible(true);

        const audio = audioRef.current;
        if (ttsJSON?.length && audio && audioURL) {
          tryStartPlayback();
          return true;
        }

        fetchTTSAssets();
        return true;
      },
      armPlaybackFromUserGesture: (): void => {
        userGestureArmedRef.current = true;
        pendingSpotlightStartRef.current = true;
        userPausedAudioRef.current = false;
        setAudioPlayerVisible(true);
        const el = audioRef.current;
        if (el) {
          void el
            .play()
            .then(() => {
              el.pause();
              el.currentTime = 0;
            })
            .catch(() => {
              /* unlock when src is set later */
            });
        }
      },
      seekToTextOffsetOnceReady: (dataElementId: string, textOffset: number): void => {
        pendingSeekRef.current = { dataElementId, textOffset };
        pendingSpotlightStartRef.current = true;
        tryStartPlayback();
      },
    }),
    [
      ttsJSON,
      audioURL,
      setAudioPlayerVisible,
      renderedId,
      spotlightAudio,
      failedIds,
      tryStartPlayback,
    ],
  );

  //=================================================================================

  return (
    <div className="dev-shell-footer">
      <div className="navigation-bar">
        {spotlightAudio && !isLessonAudioFailed(renderedId, failedIds) && (
          <audio
            className="audio-player"
            controls
            ref={audioRef}
            src={audioURL || undefined}
            preload="auto"
            onCanPlay={() => setAudioPlayerVisible(true)}
          >
            Your browser does not support the audio.
          </audio>
        )}

        <div className="navigation-bar-controls">
          <div className="navigation-button previous-button">
            {prevItem && (
              <button onClick={onPrev}>
                ❮{" "}
                <span>
                  {!!prevItem?.configurationFields && getTitle(prevItem.configurationFields)}
                </span>
              </button>
            )}
          </div>
          <input
            type="text"
            className="page-number"
            value={targetPageNumber}
            onChange={handleInputChange}
            onKeyUp={handleEnter}
          />
          <div className="navigation-button next-button">
            {nextItem && (
              <button onClick={onNext}>
                <span>
                  {!!nextItem?.configurationFields && getTitle(nextItem.configurationFields)}
                </span>{" "}
                ❯
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
